/**
 * @fileoverview System-web adapter that maps UnifiedImportWizard service calls onto system-web local-first template services and tenant-scoped import-run services.
 * @module system-web/unified-wizard-adapter
 *
 * Responsibilities:
 * - Bridge the shared import-editor service contract into system-web service implementations.
 * - Route template operations through system catalog services and run operations through tenant-scoped proxy services.
 * - Keep shared wizard code free of direct app-specific service imports.
 * - Implement the run-flow capabilities system-web supports: draft import-run
 *   persistence, split dry-run/commit pollers, the row-level explorer, and
 *   enrichment runs.
 *
 * Dependencies:
 * - `@firesquirrel/import-editor` service interfaces
 * - `systemImportTemplateService`
 * - `systemImportRunService`
 *
 * Architecture:
 * - Adapter layer only; no direct API calls and no UI state ownership.
 * - Template reads remain local-first through system-web services.
 * - Run mutations remain online-only but are still mediated by services rather than components.
 *
 * Notes:
 * - A single adapter instance is bound to one tenant context for run operations.
 * - Gate 3 publish-and-import hands off into the same run service methods exposed here.
 * - Phase 2A (G-002): the run flow now lives in `@firesquirrel/import-editor`;
 *   this adapter is the only system-web wiring it needs.
 */

import type {
  ImportWizardServices,
  ParsedFilePreview,
} from '@firesquirrel/import-editor';
import { runToProgress } from '@firesquirrel/import-editor';
import {
  listImportTemplates,
  getImportTemplate,
  createImportTemplate,
  updateImportTemplate,
} from '../services/systemImportTemplateService';
import {
  uploadImportFile,
  uploadImportFileForTemplate,
  parseImportFileForTemplate,
  createPreviewJob,
  createPreviewJobForTemplate,
  pollPreviewJob,
  pollPreviewJobForTemplate,
  createImportRun,
  pollDryRun,
  pollCommitRun,
  commitImportRun,
  cancelImportRun,
  getImportRun,
  createDraftImportRun,
  updateDraftImportRun,
  listImportRunRows,
  downloadImportRunRowsCsv,
  createEnrichmentRun,
  suggestMappings as suggestMappingsApi,
  suggestMappingsForTemplate,
  interpretColumnGroups as interpretColumnGroupsApi,
  type ParseResult,
  type ImportRunRowCategory,
} from '../services/systemImportRunService';

/** Project the host ParseResult into the shared preview shape. */
function toParsedPreview(r: ParseResult): ParsedFilePreview {
  return {
    fileType: r.fileType,
    columns: r.columns,
    sampleRows: r.sampleRows,
    totalRows: r.totalRows,
    sheets: r.availableSheets,
    activeSheet: r.activeSheet,
    hasGeometry: r.hasGeometry,
  };
}

/**
 * @contract BuildSystemAdapterOptions
 * Purpose: Bind the system-web wizard adapter to a tenant context for run-intent operations.
 * Fields: tenantId? — selected tenant; absent for tenant-agnostic template authoring.
 * Rules: Run-flow methods throw when tenantId is absent; template methods work without it.
 * Sync: Transient construction option; nothing is persisted.
 */
export interface BuildSystemAdapterOptions {
  tenantId?: string;
}

/**
 * @reusable buildSystemImportWizardServices
 * Usage: Creates the system-web service adapter consumed by UnifiedImportWizard for both authoring and run intents.
 * Rules: Stateful workflow adapter; delegates all persistence, polling, and online operations to host services and only projects shared contracts.
 */
export function buildSystemImportWizardServices(
  options: BuildSystemAdapterOptions = {},
): ImportWizardServices {
  const { tenantId } = options;
  const requireTenant = (method: string): string => {
    if (!tenantId) {
      throw new Error(
        `[UnifiedImportWizard] system adapter method '${method}' requires ` +
        `a tenantId. Resolve a tenant before routing to the run flow.`,
      );
    }
    return tenantId;
  };

  return {
    // ----- Templates ---------------------------------------------------
    listImportTemplates: () => listImportTemplates(),
    getImportTemplate: id => getImportTemplate(id),
    createImportTemplate: input => createImportTemplate(input as any),
    updateImportTemplate: (id, patch) => updateImportTemplate(id, patch as any),

    // ----- File sampling + upload pipelines ----------------------------
    parseFileForTemplate: async (file, sampleSize, sheetName, onProgress) => {
      onProgress?.(0);
      // Geo sources are sampled by the importProcessing worker, so forward the
      // worker's conversion percentage through the wizard's existing progress
      // seam. Non-geo sources are parsed inline and never report progress.
      const r = await parseImportFileForTemplate(file, sampleSize, sheetName, job => {
        onProgress?.(job.progress?.percent ?? 0);
      });
      onProgress?.(100);
      return toParsedPreview(r);
    },
    uploadImportFile: async (file, onProgress, runId) => {
      const r = tenantId
        ? await uploadImportFile(tenantId, file, onProgress, runId)
        : await uploadImportFileForTemplate(file, onProgress);
      return {
        runId: r.runId,
        blobUrl: r.blobUrl,
        filename: r.filename,
        fileSize: r.fileSize,
        fileType: r.fileType,
      };
    },
    createPreviewJob: async input => {
      if (!input.blobUrl) {
        throw new Error('createPreviewJob requires a blobUrl');
      }
      const r = tenantId
        ? await createPreviewJob(tenantId, {
            runId: input.runId,
            blobUrl: input.blobUrl,
            filename: input.filename,
            fileSize: input.fileSize,
            fileType: input.fileType,
            sheetName: input.sheetName,
            sampleSize: input.sampleSize,
          })
        : await createPreviewJobForTemplate({
            blobUrl: input.blobUrl,
            filename: input.filename,
            fileSize: input.fileSize,
            sheetName: input.sheetName,
            sampleSize: input.sampleSize,
          });
      return { jobId: r.jobId };
    },
    pollPreviewJob: async (jobId, onProgress) => {
      // The tenant-scoped poller reports the full job; the template-mode
      // poller reports only the status string. Normalize both into the
      // shared PreviewJobProgress shape.
      const r = tenantId
        ? await pollPreviewJob(tenantId, jobId, {
            intervalMs: 2_000,
            timeoutMs: 600_000,
            onProgress: job =>
              onProgress?.({
                status: job.status,
                result: job.result ? toParsedPreview(job.result) : null,
                updatedAt: job.updatedAt,
              }),
          })
        : await pollPreviewJobForTemplate(jobId, {
            intervalMs: 2_000,
            timeoutMs: 600_000,
            onProgress: status => onProgress?.({ status }),
          });
      if (!r.result) {
        throw new Error('Preview job succeeded but returned no result.');
      }
      return {
        ...toParsedPreview(r.result),
        fileType: r.fileType ?? r.result.fileType,
      };
    },

    // ----- Runs --------------------------------------------------------
    createImportRun: async req => {
      const tid = requireTenant('createImportRun');
      return createImportRun(tid, {
        runId: req.runId,
        templateId: req.templateId,
        filename: req.filename,
        blobUrl: req.blobUrl,
        isDryRun: req.isDryRun,
        importName: req.importName,
        importDescription: req.importDescription,
        importBehavior: req.importBehavior,
        matchKeyField: req.matchKeyField,
        fileSize: req.fileSize,
        fileType: req.fileType,
        columnCount: req.columnCount,
        selectedSheet: req.selectedSheet,
        boundaryCheckEnabled: req.boundaryCheckEnabled,
        dataAsOf: req.dataAsOf,
      });
    },
    pollRun: async (runId, phase, onProgress) => {
      const tid = requireTenant('pollRun');
      // System-web splits the pollers: dry runs stop at the committable
      // 'awaiting-commit' state; commits wait for full completion.
      const poller = phase === 'dryRun' ? pollDryRun : pollCommitRun;
      return poller(tid, runId, {
        intervalMs: 750,
        timeoutMs: 7_200_000,
        onProgress: run => onProgress(runToProgress(run)),
      });
    },
    commitImportRun: async runId => {
      const tid = requireTenant('commitImportRun');
      await commitImportRun(tid, runId);
    },
    cancelImportRun: async runId => {
      const tid = requireTenant('cancelImportRun');
      await cancelImportRun(tid, runId);
    },

    // ----- Draft run persistence + resume ------------------------------
    getImportRun: async runId => {
      const tid = requireTenant('getImportRun');
      return getImportRun(tid, runId);
    },
    createDraftImportRun: async input => {
      const tid = requireTenant('createDraftImportRun');
      return createDraftImportRun({ ...input, parseResult: input.parseResult as any, tenantId: tid });
    },
    updateDraftImportRun: async (runId, patch) => {
      return updateDraftImportRun(runId, patch as any);
    },

    // ----- Row-level explorer (Apr 2026 dry-run summary) ---------------
    listImportRunRows: async (runId, params) => {
      const tid = requireTenant('listImportRunRows');
      const page = await listImportRunRows(tid, runId, {
        category: params.category as ImportRunRowCategory | undefined,
        page: params.page,
        pageSize: params.pageSize as 25 | 50 | 100 | 250 | undefined,
        q: params.q,
        sort: params.sort as 'rowNumber:asc' | 'rowNumber:desc' | undefined,
        signal: params.signal,
      });
      return page;
    },
    downloadImportRunRowsCsv: async (runId, params) => {
      const tid = requireTenant('downloadImportRunRowsCsv');
      await downloadImportRunRowsCsv(tid, runId, {
        category: params.category as ImportRunRowCategory,
        q: params.q,
      });
    },

    // ----- Enrichment (Phase 5.6) --------------------------------------
    createEnrichmentRun: async params => {
      const tid = requireTenant('createEnrichmentRun');
      return createEnrichmentRun(tid, params);
    },

    // ----- Suggestions -------------------------------------------------
    suggestMappings: async req => {
      const r = tenantId
        ? await suggestMappingsApi(tenantId, {
            dataMode: req.dataMode as any,
            columns: req.columns,
            sampleRows: req.sampleRows ?? [],
            tier: (req as any).tier ?? 'both',
          })
        : await suggestMappingsForTemplate({
            dataMode: req.dataMode as any,
            columns: req.columns,
            sampleRows: req.sampleRows ?? [],
            tier: (req as any).tier ?? 'both',
          });
      return { suggestions: (r as any).suggestions ?? [] };
    },

    // ----- Column group interpretation (Phase 5.4) ---------------------
    // Tenant-scoped only: there is no template-mode (tenant-agnostic)
    // interpret endpoint on system-api, so the authoring flow keeps its
    // current behavior — the capability stays undefined without a tenant
    // and the mapping panel hides the feature.
    ...(tenantId
      ? {
          interpretColumnGroups: async (req: Parameters<NonNullable<ImportWizardServices['interpretColumnGroups']>>[0]) => {
            const r = await interpretColumnGroupsApi(tenantId, {
              dataMode: req.dataMode as any,
              columns: req.columns,
              sampleRows: req.sampleRows ?? [],
              groups: req.groups,
              autoDetect: req.autoDetect,
            });
            return {
              interpretations: (r as any).interpretations ?? [],
              used: (r as any).used ?? false,
              ...(r.error ? { error: r.error } : {}),
            };
          },
        }
      : {}),
  };
}
